"use client";

import { motion } from "framer-motion";
import { Mail, Share2 } from "lucide-react";
import React from "react";
import { Button } from "@/components/ui/button";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";

interface ShareButtonsProps {
	url: string;
	title: string;
	description?: string;
	className?: string;
}

function WhatsAppIcon({ className }: { className?: string }) {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="currentColor"
			className={className}
			role="img"
			aria-label="WhatsApp"
		>
			<title>WhatsApp</title>
			<path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
		</svg>
	);
}

function XIcon({ className }: { className?: string }) {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="currentColor"
			className={className}
			role="img"
			aria-label="X"
		>
			<title>X</title>
			<path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
		</svg>
	);
}

function FacebookIcon({ className }: { className?: string }) {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="currentColor"
			className={className}
			role="img"
			aria-label="Facebook"
		>
			<title>Facebook</title>
			<path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
		</svg>
	);
}

function InstagramIcon({ className }: { className?: string }) {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="currentColor"
			className={className}
			role="img"
			aria-label="Instagram"
		>
			<title>Instagram</title>
			<path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
		</svg>
	);
}

const socials = [
	{
		name: "WhatsApp",
		icon: WhatsAppIcon,
		color: "hover:bg-[#25D366]/10 hover:text-[#25D366]",
		getUrl: (url: string, title: string) =>
			`https://api.whatsapp.com/send?text=${encodeURIComponent(`${title} ${url}`)}`,
	},
	{
		name: "X (Twitter)",
		icon: XIcon,
		color: "hover:bg-foreground/10 hover:text-foreground",
		getUrl: (url: string, title: string) =>
			`https://x.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
	},
	{
		name: "Facebook",
		icon: FacebookIcon,
		color: "hover:bg-[#1877F2]/10 hover:text-[#1877F2]",
		getUrl: (url: string) =>
			`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
	},
	{
		name: "Instagram",
		icon: InstagramIcon,
		color: "hover:bg-[#E4405F]/10 hover:text-[#E4405F]",
		getUrl: (url: string) => url,
		action: "copy",
	},
	{
		name: "Email",
		icon: Mail,
		color: "hover:bg-primary/10 hover:text-primary",
		getUrl: (url: string, title: string, description?: string) =>
			`mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${description || title}\n\n${url}`)}`,
	},
] as const;

const containerVariants = {
	hidden: { opacity: 0 },
	visible: {
		opacity: 1,
		transition: { staggerChildren: 0.05 },
	},
};

const itemVariants = {
	hidden: { opacity: 0, scale: 0.8 },
	visible: { opacity: 1, scale: 1 },
};

export function ShareButtons({
	url,
	title,
	description,
	className,
}: ShareButtonsProps) {
	const [copied, setCopied] = React.useState(false);

	const handleShare = (social: (typeof socials)[number]) => {
		if ("action" in social && social.action === "copy") {
			navigator.clipboard.writeText(url).then(() => {
				setCopied(true);
				setTimeout(() => setCopied(false), 2000);
			});
			return;
		}
		const shareUrl = social.getUrl(url, title, description);
		window.open(shareUrl, "_blank", "noopener,noreferrer,width=600,height=400");
	};

	return (
		<TooltipProvider delayDuration={200}>
			<motion.div
				variants={containerVariants}
				initial="hidden"
				animate="visible"
				className={`flex items-center gap-2 ${className || ""}`}
			>
				<div className="flex items-center gap-1.5 text-sm text-muted-foreground mr-1">
					<Share2 className="h-4 w-4" />
					<span className="font-medium">Share</span>
				</div>
				{socials.map((social) => (
					<motion.div key={social.name} variants={itemVariants}>
						<Tooltip>
							<TooltipTrigger asChild>
								<Button
									variant="ghost"
									size="icon"
									className={`h-9 w-9 rounded-full transition-colors ${social.color}`}
									onClick={() => handleShare(social)}
									aria-label={`Share via ${social.name}`}
								>
									<social.icon className="h-4 w-4" />
								</Button>
							</TooltipTrigger>
							<TooltipContent>
								<p>
									{"action" in social && social.action === "copy"
										? copied
											? "Link copied!"
											: "Copy link for Instagram"
										: `Share on ${social.name}`}
								</p>
							</TooltipContent>
						</Tooltip>
					</motion.div>
				))}
			</motion.div>
		</TooltipProvider>
	);
}
